import { useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { cn } from '@/lib/cn'
import { useCopy } from '@/lib/use-copy'
import { QrCode } from '@/components/QrCode'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'
import { BRAND } from '@/brand/brand'

const DETAILS: Record<string, ReadonlyArray<readonly [string, string]>> = {
  USD: [['Account holder', 'Alex Turner'], ['Routing number', '084106768'], ['Account number', '9600 0012 4568'], ['Type', 'Checking']],
  EUR: [['Account holder', 'Alex Turner'], ['IBAN', 'BE71 0961 2345 6769'], ['BIC', 'TRWIBEB1XXX'], ['Bank', 'Regulated partner']],
  MXN: [['Beneficiario', 'Alex Turner'], ['CLABE', '646 180 1234 5678 90'], ['Banco', 'SPEI partner'], ['Tipo', 'Depósito']],
  BRL: [['Titular', 'Alex Turner'], ['Chave PIX', `alex@${BRAND.domain}`], ['Banco', 'PIX partner'], ['Tipo', 'Conta de pagamento']],
}

export function ReceiveScreen() {
  const repository = useRepository()
  const accounts = useQuery(() => repository.listAccounts(), [repository])
  const [currency, setCurrency] = useState('USD')
  const [showQr, setShowQr] = useState(false)
  const [copied, copy] = useCopy()

  const usable = accounts.data?.filter((account) => DETAILS[account.currency]) ?? []
  const rows = DETAILS[currency] ?? []
  const payLink = `https://${BRAND.domain}/pay/alex-turner?currency=${currency}`

  return (
    <>
      <PageHeader
        title="Receive"
        subtitle="Real local details in your name. Share them the way you would share any bank account."
        actions={
          <Button
            variant="ghost"
            size="md"
            iconStart="qr_code_2"
            onClick={() => setShowQr((value) => !value)}
          >
            {showQr ? 'Hide QR' : 'Show QR'}
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Which account?
            </Text>
            <div className={`${styles.corridors} pb-mt-4`}>
              {usable.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  className={cn(styles.corridor, account.currency === currency && styles.corridorActive)}
                  onClick={() => setCurrency(account.currency)}
                  aria-pressed={account.currency === currency}
                >
                  <CurrencyBadge currency={account.currency} size={28} />
                  <Stack gap={0}>
                    <Text variant="caption" weight="semibold">
                      {account.currency}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {account.rail}
                    </Text>
                  </Stack>
                </button>
              ))}
            </div>

            <Stack direction="row" align="center" gap={3} className="pb-mt-5">
              <Icon name="verified_user" size={20} />
              <Text variant="caption" tone="secondary">
                These details belong to you, not to a pooled account. Money sent to them lands
                directly in your balance.
              </Text>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                Your {currency} details
              </Text>
              <Pill tone="accent">Free to receive</Pill>
            </Card.Header>
            {showQr && (
              <Stack align="center" gap={3} className="pb-mt-4">
                <QrCode value={payLink} label={`Payment link for your ${currency} account`} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  Scanning opens your payment link, pre-filled in {currency}
                </Text>
              </Stack>
            )}

            <Stack gap={4} className="pb-mt-4">
              {rows.map(([label, value]) => (
                <Stack key={label} gap={1}>
                  <Text variant="eyebrow" tone="onDarkMuted">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold" tone="onDark" numeric>
                    {value}
                  </Text>
                </Stack>
              ))}
            </Stack>
            <Card.Footer>
              <Stack direction="row" gap={2} wrap>
                <Button
                  variant="primary"
                  size="md"
                  iconStart={copied ? 'check' : 'content_copy'}
                  onClick={() =>
                    copy(rows.map(([label, value]) => `${label}: ${value}`).join('\n'))
                  }
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button variant="onDark" size="md" iconStart="ios_share">
                  Share
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              What receiving costs
            </Text>
            <Stack gap={0} className="pb-mt-2">
              {[
                ['Money in, any rail', 'Free'],
                [`From another ${BRAND.name} user`, 'Free'],
                ['Salary or payroll', 'Free'],
                ['Converting once it lands', 'from 0.6%'],
              ].map(([label, value]) => (
                <div key={label} className={checkout.line}>
                  <Text variant="caption" tone="secondary">
                    {label}
                  </Text>
                  <Text variant="caption" weight="semibold">
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
